import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { IconButton, LinearProgress, NumberInput, RangeValue, Slider } from "@evergis/ui";
import { AggregationFunction, ClassificationType } from "@evergis/api";

import {
  SpecialFilterActions,
  SpecialFilterContainer,
  SpecialFilterHeader,
  SpecialFilterHeaderInfo,
  SpecialFilterHeaderInputs,
  SpecialFilterHeaderNumberInput,
  SpecialFilterHeaderWorkarea,
  SpecialFilterOverlay,
} from "./styled";

import { BAR_WIDTH, RangeStatisticsChart } from "../StatisticsCharts/RangeStatisticsChart";
import { FilledValues } from "./components/FilledValues";
import { UniqueValues } from "./components/UniqueValues";
import { SumOfValues } from "./components/SumOfValues";

import { useTableSpecialFilter } from "../../hooks/table/useTableSpecialFilter";
import { useSpecialTableFilterCondition } from "./hooks/useSpecialTableFilterCondition";
import { useContainerWidth } from "./hooks/useContainerWidth";
import { useStatisticsValues } from "../StatisticsCharts/hooks/useStatisticsValues";
import { useTableFilters } from "../../hooks/table/useTableFilters";
import { getBottomHeight } from "../../ducks/layout/selectors";

import { getStatistic } from "../../resources/statistics";
import { getClassify } from "../../resources/classify";

import { FilterConditionOperation } from "../LayerFilter/types";

// eslint-disable-next-line max-lines-per-function
export const NumberRangeFilter: FC = () => {
  const bottomHeight = useSelector(getBottomHeight);
  const [specialFilter, setSpecialFilter] = useTableSpecialFilter();
  const { layerName, attribute, filterType, selection } = specialFilter;
  const ref = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(ref);
  const chartWidth = useMemo(() => (containerWidth !== undefined ? containerWidth - 32 : undefined), [containerWidth]);
  const [barsCount, setBarsCount] = useState<number>();
  const [step, setStep] = useState<number>();
  const [slider, setSlider] = useState<RangeValue>();
  const [minValue, setMinValue] = useState<number>();
  const [maxValue, setMaxValue] = useState<number>();
  const [tableFilters, { updateFiltersByLayer }] = useTableFilters();
  const condition = useSpecialTableFilterCondition();
  const firstRender = useRef<boolean>(true);

  const resultCondition = useMemo(() => {
    return selection
      ? [`${attribute} >= ${selection[0]} && ${attribute} <= ${selection[1]}`, condition]
          .filter(Boolean)
          .join(` ${FilterConditionOperation.AND} `)
      : condition;
  }, [attribute, condition, selection]);

  const {
    data: statisicsData,
    loading: statisticsLoading,
    refetch: refetchStatistics,
  } = getStatistic.use({
    input: {
      name: layerName,
      attribute,
      condition: resultCondition,
      types: [
        AggregationFunction.Min,
        AggregationFunction.Max,
        AggregationFunction.Sum,
        AggregationFunction.Count,
        AggregationFunction.TotalCount,
        AggregationFunction.DistinctCount,
      ],
    },
    immediate: false,
  });

  const {
    data: classifyData,
    loading: classifyLoading,
    refetch: refetchClassify,
  } = getClassify.use({
    input: {
      name: layerName,
      classes: barsCount,
      type: ClassificationType.EqualInterval,
      attribute,
      condition: resultCondition,
    },
    immediate: false,
  });

  const { min, max, sum, count: statCount, totalCount, distinctCount } = useStatisticsValues(statisicsData);

  const data = useMemo(() => {
    if (!classifyData) {
      return [];
    }

    return Array.from({ length: classifyData.classifyResult.length - 1 }, (_, index) => ({
      groupName: classifyData.classifyResult[index === 0 ? index : index + 1].value,
      0: {
        value: classifyData.classifyResult[index + 1].count,
      },
    }));
  }, [classifyData]);

  const onCancel = useCallback(() => {
    setSpecialFilter({
      active: false,
    });
  }, [setSpecialFilter]);

  const onApply = useCallback(() => {
    if (minValue === undefined || maxValue === undefined || classifyData === undefined) {
      return;
    }

    updateFiltersByLayer(layerName, {
      [attribute]: {
        value: [+minValue?.toFixed(2), +maxValue?.toFixed(2)],
        filterType,
        dataType: classifyData.type,
      },
    });

    setSpecialFilter({
      active: false,
    });
  }, [attribute, classifyData, filterType, layerName, maxValue, minValue, setSpecialFilter, updateFiltersByLayer]);

  const onResetDrillDown = useCallback(() => {
    setSpecialFilter({
      ...specialFilter,
      selection: undefined,
    });

    firstRender.current = true;
  }, [setSpecialFilter, specialFilter]);

  useEffect(() => {
    if (slider === undefined && statisicsData !== undefined) {
      setSlider([min, max]);
    }
  }, [statisicsData, slider]);

  useEffect(() => {
    if (!statisicsData) {
      return;
    }

    const attributeFilter = tableFilters?.[layerName]?.[attribute];

    if (firstRender.current) {
      if (attributeFilter && Array.isArray(attributeFilter.value)) {
        setMinValue(attributeFilter.value[0] as number);
        setMaxValue(attributeFilter.value[1] as number);
        setSlider([attributeFilter.value[0] as number, attributeFilter.value[1] as number]);
      } else {
        setMinValue(min);
        setMaxValue(max);
        setSlider([min, max]);
      }
    } else {
      setMinValue(min);
      setMaxValue(max);
      setSlider([min, max]);
    }
  }, [attribute, layerName, tableFilters, statisicsData]);

  useEffect(() => {
    if (chartWidth === undefined || statisicsData === undefined) {
      return;
    }

    const count = Math.floor(chartWidth / BAR_WIDTH);

    setBarsCount(count);
    setStep(+((max - min) / count));
  }, [chartWidth, statisicsData]);

  useEffect(() => {
    if (!barsCount) {
      return;
    }

    refetchClassify();
  }, [barsCount, resultCondition]);

  useEffect(() => {
    refetchStatistics();
  }, [resultCondition]);

  return (
    <>
      <SpecialFilterOverlay bottomHeight={bottomHeight} onClick={onCancel} />
      <SpecialFilterContainer bottomHeight={bottomHeight} ref={ref}>
        {statisticsLoading || classifyLoading ? (
          <LinearProgress />
        ) : (
          <>
            <SpecialFilterHeader>
              <SpecialFilterHeaderInfo>
                <FilledValues count={statCount} totalCount={totalCount} />
                <UniqueValues distinctCount={distinctCount} />
                <SumOfValues sum={sum} />
              </SpecialFilterHeaderInfo>
              <SpecialFilterHeaderWorkarea>
                <SpecialFilterHeaderInputs>
                  <SpecialFilterHeaderNumberInput>
                    <NumberInput
                      value={+minValue?.toFixed(2)}
                      onChange={value => {
                        setMinValue(value || 0);
                        setSlider([value || 0, maxValue]);
                      }}
                      width="100%"
                    />
                  </SpecialFilterHeaderNumberInput>
                  {" - "}
                  <SpecialFilterHeaderNumberInput>
                    <NumberInput
                      value={+maxValue.toFixed(2)}
                      onChange={value => {
                        setMaxValue(value || 0);
                        setSlider([minValue, value || 0]);
                      }}
                      width="100%"
                    />
                  </SpecialFilterHeaderNumberInput>
                </SpecialFilterHeaderInputs>
                <SpecialFilterActions>
                  <IconButton kind={"close"} error onClick={onCancel} data-popover={"close"} />
                  <IconButton kind={"success"} primary onClick={onApply} data-popover={"close"} />
                  <IconButton kind={"refresh"} disabled={!selection} onClick={onResetDrillDown} />
                </SpecialFilterActions>
              </SpecialFilterHeaderWorkarea>
            </SpecialFilterHeader>
            <RangeStatisticsChart
              data={data}
              width={chartWidth}
              selectedRange={
                Array.isArray(slider) && step ? [(slider[0] - min) / step, (slider[1] - min) / step - 1] : undefined
              }
              classifyData={classifyData}
              onDrillDown={value => {
                const [drillDownMin, drillDownMax] = value;
                const initialDensity = (max - min) / chartWidth;

                firstRender.current = false;

                setSpecialFilter({
                  ...specialFilter,
                  selection: [min + initialDensity * drillDownMin, min + initialDensity * drillDownMax],
                });
              }}
            />
            <Slider
              primary
              range
              value={slider}
              min={min}
              max={max}
              disableHandlersCrossing
              onChange={(value: RangeValue) => {
                setSlider(value);
                setMinValue(value[0]);
                setMaxValue(value[1]);
              }}
              sliderWidth={containerWidth}
              showTooltips={false}
              step={step}
            />
          </>
        )}
      </SpecialFilterContainer>
    </>
  );
};
